﻿import { useState, useCallback } from "react";
import { useOccupation } from "../hooks/useOccupation";
import type { OccupationDetail, OccupationSearchItem } from "../types";

const RESEARCH_PAPERS = [
  {
    name: "OpenAI & UPenn (2023)",
    title: "GPTs are GPTs",
    desc: "1,016 个职业的 LLM 暴露度评分（beta/zeta），12 项技能回归系数（Writing +0.467***, Programming +0.623***），按 Job Zone 的教育水平分析",
    url: "https://arxiv.org/abs/2303.10130",
  },
  {
    name: "McKinsey Global Institute (2024)",
    title: "The Economic Potential of Generative AI",
    desc: "行业级 AI 经济影响分布（银行 $2,000-3,400亿/年，零售 $4,000-6,600亿/年），业务职能自动化比例，全球劳动力市场分析",
    url: "https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier",
  },
  {
    name: "WEF (2025)",
    title: "Future of Jobs Report 2025",
    desc: "2025-2030 岗位增长/衰退预测，AI+大数据、创意思维、韧性位列增长技能前三，86% 雇主预期 AI 转变业务，59% 劳动力需在 2030 年前再培训",
    url: "https://www.weforum.org/publications/the-future-of-jobs-report-2025/",
  },
  {
    name: "O*NET 29.3",
    title: "Occupational Information Network",
    desc: "美国劳工部职业数据库：1,016 个标准职业的任务构成、技能要求、知识领域、工作活动详细描述，为本评估的职业定位提供基础分类",
    url: "https://www.onetcenter.org/database.html",
  },
];

export function OccupationSearch({
  onSelect,
}: {
  onSelect: (detail: OccupationDetail) => void;
}) {
  const { results, loading, search, getDetail } = useOccupation();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleSelect = useCallback(async (item: OccupationSearchItem) => {
    setSelectedId(item.id);
    const detail = await getDetail(item.id);
    if (detail) onSelect(detail);
  }, [getDetail, onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && results.length > 0) {
      handleSelect(results[0]);
    }
  }, [results, handleSelect]);

  const showDropdown = results.length > 0 && query.length > 0;
  const noResults = !loading && query.length > 0 && results.length === 0;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="pt-24 pb-16 px-8 text-center max-w-4xl mx-auto">
        <h1 className="font-[family-name:var(--font-heading)] text-6xl font-extrabold leading-tight mb-4">
          AI 会取代你的工作吗？
        </h1>
        <p className="text-[#cbd5e1] text-lg mb-10 max-w-xl mx-auto leading-relaxed">
          基于 OpenAI、McKinsey、WEF 等权威研究，5 分钟获取你的 AI 职业风险评估
        </p>

        {/* Search */}
        <div className="relative max-w-lg mx-auto mb-16">
          <svg
            className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#64748b]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              search(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="输入职业名称，如：软件工程师、会计..."
            className="w-full pl-12 pr-12 py-4 rounded-xl bg-[#1e293b] border border-[#334155] text-[#f1f5f9] placeholder:text-[#64748b] focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:border-transparent text-base transition-shadow"
          />
          {loading && (
            <div className="absolute right-4 top-4">
              <div className="animate-spin h-5 w-5 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
            </div>
          )}
          {!loading && results.length > 0 && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <kbd className="px-1.5 py-0.5 rounded bg-[#334155] text-[#94a3b8] text-xs">&#8629;</kbd>
            </div>
          )}

          {showDropdown && (
            <ul className="absolute left-0 right-0 mt-2 bg-[#1e293b] rounded-xl border border-[#334155] shadow-[0_10px_25px_rgba(0,0,0,0.5)] overflow-hidden z-10">
              {results.map((item) => (
                <li
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className={`px-5 py-3.5 hover:bg-[#334155] cursor-pointer flex justify-between items-center transition-colors ${selectedId === item.id ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-[#3b82f6]/10 flex items-center justify-center shrink-0">
                      <svg className="h-4 w-4 text-[#60a5fa]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <div className="text-left">
                      <div className="font-medium text-[#f1f5f9]">{item.name}</div>
                      <div className="text-xs text-[#64748b]">{item.category}</div>
                    </div>
                  </div>
                  <svg className="h-5 w-5 text-[#475569] shrink-0 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </li>
              ))}
            </ul>
          )}

          {noResults && (
            <div className="absolute left-0 right-0 mt-2 bg-[#1e293b] rounded-xl border border-[#334155] p-6 text-center z-10">
              <p className="text-[#94a3b8] text-sm">
                未找到匹配的职业，请尝试其他关键词
              </p>
            </div>
          )}
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-5 gap-4 max-w-3xl mx-auto mb-16">
          {[
            { value: "1,016", label: "O*NET 职业覆盖" },
            { value: "5", label: "评估模块" },
            { value: "5", label: "份权威研究报告" },
            { value: "5-8 分钟", label: "完成评估" },
            { value: "100%", label: "数据驱动" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-[family-name:var(--font-heading)] text-2xl font-bold">{s.value}</div>
              <div className="text-xs text-[#64748b] mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-8 max-w-4xl mx-auto">
        <h2 className="font-[family-name:var(--font-heading)] text-3xl font-bold text-center mb-12">如何使用</h2>
        <div className="grid grid-cols-3 gap-8">
          {[
            { step: "1", title: "搜索你的职业", desc: "从 O*NET 1,016 个标准职业分类中找到你的岗位，查看行业 AI 影响指数" },
            { step: "2", title: "完成五维评估", desc: "5 模块 25 题，评估任务暴露度、技能脆弱度、行业冲击度、适应弹性、发展前景" },
            { step: "3", title: "获取发展报告", desc: "基于你的个人情况与权威研究对标，生成专属的职业成长行动计划" },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#3b82f6]/20 text-[#60a5fa] font-[family-name:var(--font-heading)] text-xl font-bold flex items-center justify-center mx-auto mb-4">
                {item.step}
              </div>
              <h3 className="font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-[#94a3b8] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Research Data Sources */}
      <section className="py-16 px-8 max-w-5xl mx-auto">
        <h2 className="font-[family-name:var(--font-heading)] text-3xl font-bold text-center mb-4">基于真实研究数据</h2>
        <p className="text-center text-sm text-[#64748b] mb-12 max-w-xl mx-auto">
          所有评分模型权重、行业预设、技能系数均来自可追溯的公开学术研究和权威机构报告
        </p>
        <div className="grid grid-cols-2 gap-5">
          {RESEARCH_PAPERS.map((paper) => (
            <a
              key={paper.name}
              href={paper.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-5 rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#3b82f6]/50 hover:bg-[#1e293b]/80 transition-all group"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <h3 className="font-semibold text-[#f1f5f9] text-sm">{paper.name}</h3>
                  <p className="text-xs text-[#3b82f6] mt-0.5">{paper.title}</p>
                </div>
                <svg className="h-4 w-4 text-[#475569] group-hover:text-[#3b82f6] shrink-0 mt-1 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
              <p className="text-xs text-[#94a3b8] leading-relaxed">{paper.desc}</p>
            </a>
          ))}
        </div>
        <p className="text-center text-[11px] text-[#475569] mt-6">
          另参考 WEF Future of Jobs 2023（预测基线）与 WEF Future of Jobs 2020（疫情时期技术采纳加速趋势）
        </p>
      </section>

      {/* Footer */}
      <footer className="py-12 px-8 text-center border-t border-[#1e293b]">
        <p className="text-sm text-[#64748b]">
          本站点仅基于数据做客观分析，提供成长方向。
        </p>
        <div className="mt-3 space-y-1">
          <p className="text-xs text-[#475569]">
            数据来源：
            <a href="https://arxiv.org/abs/2303.10130" target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline mx-1">OpenAI (2023)</a>
            &middot;
            <a href="https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier" target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline mx-1">McKinsey (2024)</a>
            &middot;
            <a href="https://www.weforum.org/publications/the-future-of-jobs-report-2025/" target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline mx-1">WEF 2025</a>
            &middot;
            <a href="https://www.weforum.org/publications/the-future-of-jobs-report-2023/" target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline mx-1">WEF 2023</a>
            &middot;
            <a href="https://www.onetcenter.org/database.html" target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline mx-1">O*NET</a>
          </p>
        </div>
      </footer>
    </div>
  );
}